import React, { useState } from 'react';
import TutorialGuide from '../components/TutorialGuide';
import { useNavigate } from 'react-router-dom';
import { chatCategories, chatResponses } from '../data/supportData';

/**
 * Página: SupportPage (/suporte)
 * Página dedicada de suporte com tutoriais, FAQ e acesso ao chatbot
 */
const SupportPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('tutorials');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [selectedFaqCategory, setSelectedFaqCategory] = useState('owner');

  const tabs = [
    { id: 'tutorials', label: '📖 Tutoriais', description: 'Aprenda passo a passo' },
    { id: 'faq', label: '❓ Perguntas Frequentes', description: 'Respostas rápidas' },
  ];

  const toggleFaq = (id) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  // Formatar texto com **bold** e \n
  const formatText = (text) => {
    const parts = text.split(/(\*\*.*?\*\*|\n)/g);
    return parts.map((part, i) => {
      if (part === '\n') return <br key={i} />;
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong key={i} className="font-semibold">
            {part.slice(2, -2)}
          </strong>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 sm:px-4 py-1 sm:py-1.5 text-xs sm:text-sm mb-4 sm:mb-6">
              <span>🤖</span>
              <span>Centro de Ajuda</span>
            </div>
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
              Como podemos ajudar?
            </h1>
            <p className="text-sm sm:text-lg text-indigo-200 mb-6 sm:mb-8 px-2">
              Encontre tutoriais, respostas rápidas e tudo o que precisa para tirar o máximo proveito do Barberize
            </p>
            
            {/* Quick Links */}
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-2 sm:gap-3 px-4 sm:px-0">
              <button
                onClick={() => setActiveTab('tutorials')}
                className="bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all"
              >
                📖 Ver Tutoriais
              </button>
              <button
                onClick={() => setActiveTab('faq')}
                className="bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all"
              >
                ❓ Perguntas Frequentes
              </button>
              <button
                onClick={() => navigate('/contato')}
                className="bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all"
              >
                📧 Contactar Suporte
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10">
        {/* Tabs */}
        <div className="flex gap-0 sm:gap-2 mb-6 sm:mb-8 border-b border-gray-200 overflow-x-auto scrollbar-hide">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 sm:px-5 py-2.5 sm:py-3 text-xs sm:text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content: Tutoriais */}
        {activeTab === 'tutorials' && (
          <div>
            <div className="mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Tutoriais Interativos</h2>
              <p className="text-gray-500 mt-1 text-sm sm:text-base">Aprenda a usar a plataforma com guias passo a passo</p>
            </div>
            <TutorialGuide />
          </div>
        )}

        {/* Tab Content: FAQ */}
        {activeTab === 'faq' && (
          <div>
            <div className="mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Perguntas Frequentes</h2>
              <p className="text-gray-500 mt-1 text-sm sm:text-base">Respostas rápidas para as dúvidas mais comuns</p>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-4 sm:mb-6">
              {chatCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => { setSelectedFaqCategory(cat.id); setExpandedFaq(null); }}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all duration-200 ${
                    selectedFaqCategory === cat.id
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* FAQ Accordion */}
            <div className="space-y-3 max-w-3xl">
              {chatResponses[selectedFaqCategory]?.map((qa) => (
                <div
                  key={qa.id}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden transition-all duration-200"
                >
                  <button
                    onClick={() => toggleFaq(qa.id)}
                    className="w-full text-left px-3 sm:px-5 py-3 sm:py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-sm sm:text-base text-gray-900 pr-3 sm:pr-4">{qa.question}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`text-gray-400 transition-transform duration-200 flex-shrink-0 ${
                        expandedFaq === qa.id ? 'rotate-180' : ''
                      }`}
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </button>
                  {expandedFaq === qa.id && (
                    <div className="px-3 sm:px-5 pb-3 sm:pb-4 text-xs sm:text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3">
                      {formatText(qa.answer)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-10 sm:mt-16 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl sm:rounded-2xl p-5 sm:p-8 text-center border border-indigo-100">
          <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-2">
            Não encontrou o que procurava?
          </h3>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
            Use o chatbot no canto inferior direito para ajuda instantânea ou entre em contacto connosco
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-2 sm:gap-3">
            <button
              onClick={() => navigate('/contato')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 sm:px-6 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-colors shadow-sm"
            >
              📧 Contactar Suporte
            </button>
            <button
              onClick={() => {
                // Abrir chatbot programaticamente (dispatch custom event)
                window.dispatchEvent(new CustomEvent('open-chatbot'));
              }}
              className="bg-white hover:bg-gray-50 text-gray-700 px-5 sm:px-6 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-colors shadow-sm border border-gray-200"
            >
              🤖 Abrir Chatbot
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
